/**
 * Row-by-row parsing for a response whose payload is a collection — a partial
 * failure inside a bag of independent rows must cost that one row, never the
 * whole response. `contract.ts` next door parses a response ATOMICALLY (the
 * right call for a spine or an envelope, where one malformed field means the
 * caller's own model is out of date); this module is for the other shape —
 * a `Record<string, T>` or a `T[]` whose entries are independently useful and
 * independently allowed to drift, additively, ahead of this client.
 *
 * Kept separate from `contract.ts` rather than folded in because
 * `reportDroppedRowsOnce` below needs `@/lib/sentry/ui-error-reporter`, and
 * `contract.ts`'s docblock states it is runtime-pure on purpose — pulling a
 * Sentry client into that seam would be exactly the widening it warns against.
 *
 * ## Scope: the three BQL read routes, not all four
 *
 * `GET /bql/schema`, `POST /bql` and `GET /bql/fields` are built on this —
 * see `shared/bql/schema-contract.ts`, `shared/bql/batch-contract.ts` and
 * `shared/bql/fields-contract.ts`. **`GET /bql/values` is deliberately NOT
 * built on this and never should be**: its payload is
 * `CommonValuesResultSchema.values: z.array(z.unknown())` — the server never
 * types the values it returns, so there is no enum surface for a client
 * schema to fall behind in the first place. Building a partial-parse path for
 * a field that is `unknown` by contract would be a helper with no failure
 * mode to catch.
 *
 * ## What a caller keeps for itself
 *
 * This module owns row-by-row parsing and once-per-session reporting; it does
 * NOT own any wire shape. Each of the three sites above keeps its own
 * hand-written ENVELOPE schema — the generated schema loosened in exactly the
 * places its rows get re-gated by `parseRecordRows`/`parseArrayRows` (an
 * entity, a field, an `operator_legality` entry, a tile) — with its own
 * `satisfies` proof back to the generated type, the same pattern
 * `custom-dashboards/wire-schema.ts` already uses for `widgets`. A schema that
 * loosens a key this module never re-gates is a bug in the caller, not
 * something this module can catch for it.
 */
import { captureException } from '@/lib/sentry/ui-error-reporter'

import type { ResponseContractError } from './contract'
import type { UIErrorContext } from '@/lib/sentry/ui-error-reporter'
import type { z } from 'zod'

/** One row that failed its own schema and was dropped so its siblings could parse. */
export type DroppedRow = {
	/** Where the row lived — an entity name, a field id, a `query_id`, an `operator_legality` key. Dotted to the row's position for `entities.cases.fields.weird`-style precision. */
	key: string
	error: ResponseContractError
}

/** The result of a row-by-row parse: what survived, and what was dropped along the way. */
export type PartialParse<T> = {
	value: T
	dropped: DroppedRow[]
}

/**
 * Parse a `Record<string, unknown>` one entry at a time. An entry whose VALUE
 * fails `valueSchema` is dropped and named in `dropped`; every other entry
 * still parses.
 *
 * `keySchema`, when given, gates the KEY too — before the value is even
 * looked at. This exists for exactly one reason: `operator_legality` is a
 * `Dict[FieldType, List[Operator]]`, and the generated `.partialRecord()`
 * rejects an unknown KEY the same as an unknown value. Without a way to
 * express "drop this row because its key isn't a known `FieldType`", the row
 * carrying an unrecognised type (`geo_point`) could only be caught by
 * rejecting the whole map — which is the exact failure this helper exists to
 * avoid. Passing a key schema is what proves the shape can express that case.
 */
export function parseRecordRows<T>(
	record: Record<string, unknown>,
	valueSchema: z.ZodType<T>,
	fail: (key: string, cause: z.ZodError) => ResponseContractError,
	keySchema?: z.ZodType<string>,
): PartialParse<Record<string, T>> {
	const value: Record<string, T> = {}
	const dropped: DroppedRow[] = []

	for (const [key, raw] of Object.entries(record)) {
		if (keySchema) {
			const keyResult = keySchema.safeParse(key)
			if (!keyResult.success) {
				dropped.push({ key, error: fail(key, keyResult.error) })
				continue
			}
		}

		const result = valueSchema.safeParse(raw)
		if (result.success) {
			value[key] = result.data
			continue
		}

		dropped.push({ key, error: fail(key, result.error) })
	}

	return { value, dropped }
}

/**
 * Parse an array one element at a time. An element that fails `schema` is
 * dropped and named in `dropped` by `keyOf` (falling back to its index);
 * every other element still parses, in its original order.
 */
export function parseArrayRows<T>(
	array: readonly unknown[],
	schema: z.ZodType<T>,
	fail: (key: string, cause: z.ZodError) => ResponseContractError,
	keyOf: (raw: unknown, index: number) => string = (_raw, index) =>
		String(index),
): PartialParse<T[]> {
	const value: T[] = []
	const dropped: DroppedRow[] = []

	array.forEach((raw, index) => {
		const result = schema.safeParse(raw)
		if (result.success) {
			value.push(result.data)
			return
		}

		dropped.push({
			key: keyOf(raw, index),
			error: fail(keyOf(raw, index), result.error),
		})
	})

	return { value, dropped }
}

/**
 * Build a `parseArrayRows` `keyOf` that reads a named string field off an
 * otherwise-`unknown` row, falling back to the row's array index when the
 * field is missing or not a string.
 *
 * Extracted because all three BQL read seams needed the exact same shape —
 * `schema-contract.ts` and `fields-contract.ts` both name a `FieldDescriptor`
 * by its `id`, `batch-contract.ts` names a tile by its `query_id` — and three
 * copies of the same `typeof raw === 'object' && …` guard is the duplication
 * this module exists to prevent elsewhere.
 */
export const rowKeyOf =
	(field: string) =>
	(raw: unknown, index: number): string =>
		typeof raw === 'object' &&
		raw !== null &&
		field in raw &&
		typeof (raw as Record<string, unknown>)[field] === 'string'
			? ((raw as Record<string, unknown>)[field] as string)
			: String(index)

/**
 * A dropped row is a contract mismatch — a bug, exactly like the ones
 * `contract.ts` throws for. It just must not be allowed to cost its
 * siblings, so it is reported instead of thrown.
 *
 * Deduplicated for the life of the tab, keyed by `context.action` + the row's
 * own `key` + its error message: the same route re-polling every 30s (or
 * refetching on window focus) must not re-report the same broken row every
 * time, but the SAME row failing a NEW way (a regen narrowed a different
 * field) is a different bug and is worth a second event — the same shape
 * `custom-dashboards/repository.ts` already uses for `reportedWidgetDrops`.
 *
 * **Guardrail:** this is Sentry-only, by design (Q12 in `rulings.md`) — a
 * dropped row never gets its own per-field UI disclosure or a bespoke error
 * taxonomy. A caller the descriptor never offered a field for is not
 * "missing" anything from that caller's point of view; it is the same shape
 * as the field never having existed, which every consuming surface already
 * renders correctly with no new state. If this ever grows a disclosure UI and
 * its own error codes, it has outgrown the risk it mitigates — an additive
 * enum value silently narrowing one row, not a systemic outage — and needs a
 * different design, not an extension of this function.
 */
const reportedRows = new Set<string>()

export function reportDroppedRowsOnce(
	dropped: readonly DroppedRow[],
	context: Omit<UIErrorContext, 'entityId'>,
): void {
	for (const row of dropped) {
		const dedupeKey = `${context.feature}:${context.action}:${row.key}:${row.error.message}`
		if (reportedRows.has(dedupeKey)) continue
		reportedRows.add(dedupeKey)

		captureException(row.error, { ...context, entityId: row.key })
	}
}
